// C:\Antti\aws\amcore-app\src\Dashboard.js

import React, { useEffect, useRef, useState } from 'react';
import { API } from 'aws-amplify';
import {
  BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
  LabelList
} from 'recharts';
import GaugeChart from 'react-gauge-chart';
import './Dashboard.css';
import { Auth } from 'aws-amplify';
import { useNavigate } from 'react-router-dom';
import { clearAuthUser } from './authUserStore';

// PDF kirjastot
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/* -------------------------------------------------
   1) Apufunktiot
---------------------------------------------------*/
function formatDay(isoString) {
  try {
    const dateObj = new Date(isoString);
    const dayNames = [
      'Sunnuntai','Maanantai','Tiistai','Keskiviikko','Torstai','Perjantai','Lauantai'
    ];
    const dd = String(dateObj.getDate()).padStart(2,'0');
    const mm = String(dateObj.getMonth()+1).padStart(2,'0');
    const yyyy = String(dateObj.getFullYear());
    const weekdayName = dayNames[dateObj.getDay()] || '';
    return `${dd}.${mm}.${yyyy} (${weekdayName})`;
  } catch (err) {
    console.error('Virhe päivämäärän jäsentämisessä:', isoString, err);
    return isoString;
  }
}

// "YYYY-MM-DD" => "DD.MM.YYYY"
function formatYMDtoDMY(ymd) {
  if (!ymd) return ymd;
  const parts = ymd.split('-'); // [yyyy, mm, dd]
  if (parts.length !== 3) return ymd;
  const [y, m, d] = parts;
  return `${d}.${m}.${y}`;
}

// mode=day => "YYYY-MM-DD", mode=range => "YYYY-MM-DD - YYYY-MM-DD"
function formatDateOrRange(str, mode) {
  if (!str) return str;
  if (mode === 'day') {
    return formatYMDtoDMY(str);
  } else {
    const [start, end] = str.split(' - ');
    return formatYMDtoDMY(start) + ' - ' + formatYMDtoDMY(end);
  }
}

function parseHourBucket(tsString) {
  return parseInt(tsString.substring(11, 13), 10);
}

function generateHourSlots() {
  const arr = [];
  for (let h=0; h<24; h++){
    arr.push({ hourNum:h, eventCount:0 });
  }
  return arr;
}

function GaugeMeter({ value, maxValue }) {
  const safeValue = Math.max(0, Math.min(value, maxValue));
  const percent   = safeValue / maxValue;
  return (
    <div className="gauge-meter-container">
      <GaugeChart
        id="dark-gauge"
        nrOfLevels={20}
        percent={percent}
        colors={['#444','#ff4f59']}
        arcWidth={0.2}
        animDuration={800}
        animDelay={0}
        hideText={true}
      />
      <div className="gauge-meter-readout">
        {safeValue.toFixed(0)}
      </div>
    </div>
  );
}

/* -------------------------------------------------
   3) Dashboard
---------------------------------------------------*/
export default function Dashboard() {
  const navigate = useNavigate();
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [userGroups, setUserGroups] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');

  // ✅ Debug toggle + debug output
  const [debugEnabled, setDebugEnabled] = useState(false);
  const [lastDebug, setLastDebug] = useState(null);
  const [lastCustomQueryMeta, setLastCustomQueryMeta] = useState(null);

  // Asetukset
  const [openHours, setOpenHours] = useState({
    mondayOpen:10,mondayClose:20,
    tuesdayOpen:10,tuesdayClose:20,
    wednesdayOpen:10,wednesdayClose:20,
    thursdayOpen:10,thursdayClose:20,
    fridayOpen:10, fridayClose:20,
    saturdayOpen:10, saturdayClose:19,
    sundayOpen:12, sundayClose:18
  });
  const [usageDate, setUsageDate] = useState('2025-03-08');

  // Myymälä
  const [store, setStore] = useState('iso');

  // Data
  const [defaultData, setDefaultData] = useState([]);
  const [yesterdayCount, setYesterdayCount] = useState(0);
  const [sevenDaysCount, setSevenDaysCount] = useState(0);
  const [thirtyDaysCount, setThirtyDaysCount] = useState(0);

  const [customStart, setCustomStart] = useState('');
  const [customEnd,   setCustomEnd]   = useState('');
  const [customData,  setCustomData]  = useState([]);
  const [customQueriedOnce, setCustomQueriedOnce] = useState(false);

  const [selectedDay, setSelectedDay] = useState('');
  const [hourlyData,  setHourlyData]  = useState([]);

  const [showSettingsModal, setShowSettingsModal] = useState(false);

  // KONVERSIOLASKURI
  const [showConversion, setShowConversion] = useState(false);
  const [conversionHistory, setConversionHistory] = useState([]);

  useEffect(()=>{
    const saved= localStorage.getItem('dashboardSettings');
    if(saved){
      try{
        const parsed= JSON.parse(saved);
        if(parsed.openHours) setOpenHours(parsed.openHours);
        if(parsed.usageDate) setUsageDate(parsed.usageDate);
      } catch(e){
        console.warn('LocalStorage parse error', e);
      }
    }
    const conv= localStorage.getItem('conversionHistory');
    if(conv){
      try{
        const parsed= JSON.parse(conv);
        setConversionHistory(parsed);
      }catch(e){
        console.warn('conversionHistory parse error', e);
      }
    }
    setLoadingSettings(false);
  },[]);

  useEffect(()=>{
    Auth.currentAuthenticatedUser()
      .then(user=>{
        const groups= user?.signInUserSession?.idToken?.payload?.['cognito:groups']||[];
        setUserGroups(groups);
        setLoadingGroups(false);
      })
      .catch(err=>{
        console.error('Ryhmien lukemisvirhe:',err);
        setErrorMessage('Virhe ryhmien lukemisessa.');
        setLoadingGroups(false);
      });
  },[]);

  const canAccessForum=
    userGroups.includes('juhlamaailma_admin')|| userGroups.includes('juhlamaailma_forum');
  const canAccessIso=
    userGroups.includes('juhlamaailma_admin')|| userGroups.includes('juhlamaailma_isoomena');

  useEffect(()=>{
    if(!loadingGroups){
      if(canAccessForum && !canAccessIso){
        setStore('forum');
      } else if(canAccessIso && !canAccessForum){
        setStore('iso');
      } else if(!canAccessForum && !canAccessIso){
        setErrorMessage('Sinulla ei ole oikeuksia datanäkymään');
      }
    }
  },[loadingGroups, canAccessForum, canAccessIso]);

  useEffect(()=>{
    if(!loadingGroups && !loadingSettings){
      refreshAllData();
    }
    // eslint-disable-next-line
  },[loadingGroups, loadingSettings, store]);

  /* Data-funktiot */
  function buildQueryStringParams(){
    const qs={...openHours};
    if(usageDate){
      qs.usageDate=usageDate;
    }
    return { queryStringParameters: qs };
  }

  // ✅ Helper: debug-muodon purku { data, _debug } vs array
  function unwrapResponse(payload){
    if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
      if (Array.isArray(payload.data)) return { data: payload.data, debug: payload._debug || null };
    }
    return { data: payload, debug: null };
  }

  function fetchData(path, callback){
    const config= buildQueryStringParams();

    // lokitus helpottaa heti
    console.log('[API] GET', path, config);

    API.get('TimestreamApi', path, config)
      .then(response=>{
        console.log('[API] OK', path, response);
        callback(response);
        setErrorMessage('');
      })
      .catch(err=>{
        console.error('[API] ERR', path, err);
        // Amplify errorit ovat joskus monikerroksisia
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          'Virhe datan haussa';
        setErrorMessage(msg);
      });
  }

  function fetchDefault(){
    if(!store)return;
    fetchData(`/items?store=${store}`, res=>{
      const { data } = unwrapResponse(res);
      if (Array.isArray(data)) {
        data.forEach(d=> {
          d.dayBucket= formatDay(d.dayBucket);
        });
        setDefaultData(data);
      } else {
        setDefaultData([]);
      }
    });
  }

  function fetchYesterday(){
    if(!store)return;
    fetchData(`/items?mode=yesterday&store=${store}`, res=>{
      const { data } = unwrapResponse(res);
      let total=0;
      if(Array.isArray(data)){
        total= data.reduce((acc,row)=> acc+row.eventCount,0);
      }
      setYesterdayCount(total);
    });
  }

  function fetchLast7(){
    if(!store)return;
    fetchData(`/items?mode=last7&store=${store}`, res=>{
      const { data } = unwrapResponse(res);
      let total=0;
      if(Array.isArray(data)){
        total= data.reduce((acc,row)=> acc+row.eventCount,0);
      }
      setSevenDaysCount(total);
    });
  }

  function fetchLast30(){
    if(!store)return;
    fetchData(`/items?mode=last30&store=${store}`, res=>{
      const { data } = unwrapResponse(res);
      let total=0;
      if(Array.isArray(data)){
        total= data.reduce((acc,row)=> acc+row.eventCount,0);
      }
      setThirtyDaysCount(total);
    });
  }

  function refreshAllData(){
    fetchDefault();
    fetchYesterday();
    fetchLast7();
    fetchLast30();
  }

  function handleCustomSearch(){
    if(!customStart||!customEnd){
      setErrorMessage('Valitse sekä alku- että loppupäivä');
      return;
    }

    setCustomQueriedOnce(true);
    setLastCustomQueryMeta({ start: customStart, end: customEnd, store, debugEnabled });
    setLastDebug(null);

    const debugParam = debugEnabled ? '&debug=1' : '';
    const path=`/items?start=${customStart}&end=${customEnd}&store=${store}${debugParam}`;

    fetchData(path, payload=>{
      const { data, debug } = unwrapResponse(payload);

      if (debug) {
        setLastDebug(debug);
        console.log('[RANGE _debug]', debug);
      }

      if (Array.isArray(data)) {
        data.forEach(d=>{ d.dayBucket=formatDay(d.dayBucket); });
        setCustomData(data);
      } else {
        setCustomData([]);
      }
    });
  }

  function handleFetchHourly(){
    if(!selectedDay){
      setErrorMessage('Valitse ensin päivä');
      return;
    }

    setLastDebug(null);

    const debugParam = debugEnabled ? '&debug=1' : '';
    const path=`/items?mode=hourly&day=${selectedDay}&store=${store}${debugParam}`;
    const hoursArr= generateHourSlots();

    fetchData(path, payload=>{
      const { data, debug } = unwrapResponse(payload);

      if (debug) {
        setLastDebug(debug);
        console.log('[HOURLY _debug]', debug);
      }

      if (Array.isArray(data)) {
        data.forEach(item=>{
          const h=parseHourBucket(item.hourBucket);
          if(h>=0 && h<24){
            hoursArr[h].eventCount= item.eventCount;
          }
        });
      }

      setHourlyData(hoursArr);
    });
  }

  /* Logout */
  const handleLogout= async()=>{
    try{
      await Auth.signOut();
      clearAuthUser();
      navigate('/');
    }catch(e){
      console.error('logout error',e);
    }
  };

  /* Asetukset */
  function handleOpenModal(){
    setShowSettingsModal(true);
  }
  function handleCloseModal(){
    setShowSettingsModal(false);
  }
  function handleSaveSettings(){
    const settingsObj={ openHours, usageDate };
    localStorage.setItem('dashboardSettings', JSON.stringify(settingsObj));
    setShowSettingsModal(false);
    refreshAllData();
  }
  function handleOpenHoursChange(e){
    const { name, value }= e.target;
    setOpenHours(prev=>({
      ...prev,
      [name]: value? parseInt(value,10):0
    }));
  }
  function handleUsageDateChange(e){
    setUsageDate(e.target.value);
  }

  /* PDF-luonti */
  async function handleExportPdf(elementId, pdfFileName){
    const inputElem= document.getElementById(elementId);
    if(!inputElem){
      console.error('element not found',elementId);
      return;
    }
    try{
      const canvas= await html2canvas(inputElem);
      const imgData= canvas.toDataURL('image/png');
      const pdf= new jsPDF('p','pt','a4');
      const pageWidth= pdf.internal.pageSize.getWidth();
      const imgWidth=canvas.width;
      const imgHeight=canvas.height;
      const ratio= imgHeight/imgWidth;
      const printWidth= pageWidth;
      const printHeight= printWidth*ratio;
      pdf.addImage(imgData,'PNG',0,0,printWidth,printHeight);
      pdf.save(pdfFileName||'report.pdf');
    } catch(err){
      console.error('PDF error', err);
    }
  }

  /* -------------------------------------------------
     13) KONVERSIOLASKURI
  ---------------------------------------------------*/
  const [convDate, setConvDate] = useState('');
  const [convSales, setConvSales]= useState('');
  const [convStart, setConvStart]= useState('');
  const [convEnd, setConvEnd]   = useState('');
  const [convRangeSales, setConvRangeSales]= useState('');

  function openConversionCalculator(){
    setShowConversion(true);
  }
  function closeConversionCalculator(){
    setShowConversion(false);
  }

  // Rivin poisto
  function handleRemoveConversion(index){
    const newHist= [...conversionHistory];
    newHist.splice(index,1);
    setConversionHistory(newHist);
    localStorage.setItem('conversionHistory', JSON.stringify(newHist));
  }

  function handleCalculateDayConversion(){
    if(!convDate||!convSales){
      alert('Valitse päivä ja syötä myyntitapahtumat');
      return;
    }

    const debugParam = debugEnabled ? '&debug=1' : '';
    const path=`/items?mode=hourly&day=${convDate}&store=${store}${debugParam}`;

    fetchData(path, payload=>{
      const { data, debug } = unwrapResponse(payload);
      if (debug) console.log('[CONV DAY _debug]', debug);

      let sum=0;
      if (Array.isArray(data)) {
        data.forEach(r=> sum+= r.eventCount);
      }

      const visitors= sum;
      const sales= parseInt(convSales,10);
      const conversion= (visitors>0)? (sales/visitors)*100:0;
      const storeName= store==='iso'?'Iso-Omena':'Forum';

      const entry={
        mode:'day',
        dateOrRange: convDate,
        visitors,
        sales,
        storeName,
        conversion: parseFloat(conversion.toFixed(2))
      };
      const newHist= [...conversionHistory, entry];
      setConversionHistory(newHist);
      localStorage.setItem('conversionHistory', JSON.stringify(newHist));
    });
  }

  function handleCalculateRangeConversion(){
    if(!convStart||!convEnd||!convRangeSales){
      alert('Valitse alku/loppu ja syötä myyntitapahtumat');
      return;
    }

    const debugParam = debugEnabled ? '&debug=1' : '';
    const path=`/items?start=${convStart}&end=${convEnd}&store=${store}${debugParam}`;

    fetchData(path, payload=>{
      const { data, debug } = unwrapResponse(payload);
      if (debug) console.log('[CONV RANGE _debug]', debug);

      let sum=0;
      if (Array.isArray(data)) {
        data.forEach(r=> sum+= r.eventCount);
      }

      const visitors= sum;
      const sales= parseInt(convRangeSales,10);
      const conversion= (visitors>0)? (sales/visitors)*100:0;
      const storeName= store==='iso'?'Iso-Omena':'Forum';

      const entry={
        mode:'range',
        dateOrRange: `${convStart} - ${convEnd}`,
        visitors,
        sales,
        storeName,
        conversion: parseFloat(conversion.toFixed(2))
      };
      const newHist= [...conversionHistory, entry];
      setConversionHistory(newHist);
      localStorage.setItem('conversionHistory', JSON.stringify(newHist));
    });
  }

  if(loadingGroups||loadingSettings){
    return(
      <div className="dashboard-container">
        <div className="dashboard-sidebar">
          <div className="sidebar-logo">
            <img src="/stcloud1.png" alt="ST Cloud Logo"/>
          </div>
          <button className="logout-button" onClick={handleLogout}>
            Kirjaudu ulos
          </button>
        </div>
        <div className="dashboard-main">
          <h2>Ladataan tietoja...</h2>
        </div>
      </div>
    );
  }

  return(
    <div className="dashboard-container">
      <div className="dashboard-sidebar">
        <div className="sidebar-logo">
          <img src="/stcloud1.png" alt="ST Cloud Logo"/>
        </div>
        <button className="logout-button" onClick={handleLogout}>
          Kirjaudu ulos
        </button>
      </div>

      <div className="dashboard-main" id="dashboard-main">
        <div className="dashboard-topbar" style={{ gap:'1rem'}}>
          {(canAccessForum && canAccessIso) && (
            <>
              <button onClick={()=> setStore(store==='iso'?'forum':'iso')}>
                {store==='iso'?'Siirry Forum myymälään':'Siirry Iso-Omenan myymälään'}
              </button>
              <p>Nykyinen: <strong>{store==='iso'?'Iso-Omena':'Forum'}</strong></p>
            </>
          )}

          {/* ✅ Debug toggle */}
          <label style={{ display:'flex', alignItems:'center', gap:'8px', cursor:'pointer' }}>
            <input
              type="checkbox"
              checked={debugEnabled}
              onChange={(e)=> setDebugEnabled(e.target.checked)}
            />
            Debug
          </label>

          <button onClick={handleOpenModal}>Asetukset</button>
          <button onClick={refreshAllData}>Päivitä</button>
          <button onClick={()=> handleExportPdf('dashboard-main','koko_sivu.pdf')}>
            Luo PDF (koko sivu)
          </button>
          <button onClick={openConversionCalculator}>
            Konversiolaskuri
          </button>
        </div>

        {errorMessage && <p className="error-message">{errorMessage}</p>}

        {!canAccessForum && !canAccessIso &&(
          <div style={{ color:'red', margin:'20px'}}>
            <h2>Ei oikeutta katsella dataa</h2>
          </div>
        )}

        {store && (
          <>
            {/* 30p data */}
            <div className="dashboard-section" id="kuukauden-kavijat">
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                <h2>Kuukauden kävijät ({store==='iso'?'Iso-Omena':'Forum'})</h2>
                <button onClick={()=> handleExportPdf('kuukauden-kavijat','kuukauden_kavijat.pdf')}>
                  Luo PDF
                </button>
              </div>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={defaultData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#666"/>
                    <XAxis dataKey="dayBucket" stroke="#eee"/>
                    <YAxis stroke="#eee"/>
                    <Tooltip
                      contentStyle={{ backgroundColor:'#222', border:'none'}}
                      labelStyle={{ color:'#fff'}}
                      itemStyle={{ color:'#fff'}}
                    />
                    <Bar dataKey="eventCount" fill="#ff3636" name="Kävijät">
                      <LabelList
                        dataKey="eventCount"
                        position="insideBottom"
                        offset={6}
                        fill="#fff"
                        fontSize={12}
                        formatter={(v) => (v > 0 ? v : '')}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="dashboard-section" id="mittarit-container" style={{ position:'relative'}}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                <h2>Mittarit ({store==='iso'?'Iso-Omena':'Forum'})</h2>
                <button onClick={()=> handleExportPdf('mittarit-container','mittarit.pdf')}>
                  Luo PDF
                </button>
              </div>
              <div className="triple-gauge-container">
                <div className="gauge-meter-wrapper">
                  <h2>Eiliset kävijät: <strong>{yesterdayCount}</strong></h2>
                  <GaugeMeter value={yesterdayCount} maxValue={2000}/>
                </div>
                <div className="gauge-meter-wrapper">
                  <h2>Kävijät 7-päivän ajalta: <strong>{sevenDaysCount}</strong></h2>
                  <GaugeMeter value={sevenDaysCount} maxValue={14000}/>
                </div>
                <div className="gauge-meter-wrapper">
                  <h2>Kävijät 30-päivän ajalta: <strong>{thirtyDaysCount}</strong></h2>
                  <GaugeMeter value={thirtyDaysCount} maxValue={60000}/>
                </div>
              </div>
            </div>

            <div className="dashboard-section" id="custom-range">
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                <h2>Kävijät valitulta aikaväliltä ({store==='iso'?'Iso-Omena':'Forum'})</h2>
                <button onClick={()=> handleExportPdf('custom-range','custom_range.pdf')}>
                  Luo PDF
                </button>
              </div>

              <div style={{ display:'flex', gap:'10px', alignItems:'center', marginBottom:'10px'}}>
                <label>
                  Alku
                  <input
                    type="date"
                    value={customStart}
                    onChange={(e)=> setCustomStart(e.target.value)}
                  />
                </label>
                <label>
                  Loppu
                  <input
                    type="date"
                    value={customEnd}
                    onChange={(e)=> setCustomEnd(e.target.value)}
                  />
                </label>
                <button onClick={handleCustomSearch}>Hae</button>
              </div>

              {/* ✅ Selkeä viesti, jos haettu mutta dataa ei ole */}
              {customQueriedOnce && Array.isArray(customData) && customData.length === 0 && (
                <p style={{ color:'#ccc', marginTop:'10px' }}>
                  Ei dataa valitulla aikavälillä ({formatYMDtoDMY(customStart)} – {formatYMDtoDMY(customEnd)}).
                </p>
              )}

              {/* ✅ Debug-paneeli */}
              {debugEnabled && (lastDebug || lastCustomQueryMeta) && (
                <div style={{
                  background:'#151515',
                  border:'1px solid #333',
                  borderRadius:'10px',
                  padding:'10px',
                  marginTop:'10px',
                  color:'#ddd',
                  fontSize:'0.9em'
                }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <strong>Debug</strong>
                    <button
                      style={{ fontSize:'0.85em' }}
                      onClick={() => setLastDebug(null)}
                    >
                      Tyhjennä
                    </button>
                  </div>

                  {lastCustomQueryMeta && (
                    <div style={{ marginTop:'6px' }}>
                      <div><strong>Query:</strong> store={lastCustomQueryMeta.store}, {lastCustomQueryMeta.start} → {lastCustomQueryMeta.end}, debug={String(lastCustomQueryMeta.debugEnabled)}</div>
                    </div>
                  )}

                  {lastDebug ? (
                    <pre style={{
                      marginTop:'8px',
                      whiteSpace:'pre-wrap',
                      wordBreak:'break-word',
                      color:'#cfcfcf'
                    }}>
{JSON.stringify(lastDebug, null, 2)}
                    </pre>
                  ) : (
                    <div style={{ marginTop:'8px', color:'#aaa' }}>
                      Ei _debug-dataa vielä. Varmista että backend on päivitetty ja debug on päällä ennen hakua.
                    </div>
                  )}
                </div>
              )}

              {/* Chart näytetään kun dataa on */}
              {Array.isArray(customData) && customData.length > 0 && (
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={customData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#666"/>
                      <XAxis dataKey="dayBucket" stroke="#eee"/>
                      <YAxis stroke="#eee"/>
                      <Tooltip
                        contentStyle={{ backgroundColor:'#222', border:'none'}}
                        labelStyle={{ color:'#fff'}}
                        itemStyle={{ color:'#fff'}}
                      />
                      <Bar dataKey="eventCount" fill="#ff3636" name="Kävijät">
                        <LabelList
                          dataKey="eventCount"
                          position="insideBottom"
                          offset={6}
                          fill="#fff"
                          fontSize={12}
                          formatter={(v) => (v > 0 ? v : '')}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <div className="dashboard-section" id="tuntidata">
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                <h2>Valitun päivän kävijät ({store==='iso'?'Iso-Omena':'Forum'})</h2>
                <button onClick={()=> handleExportPdf('tuntidata','tuntidata.pdf')}>
                  Luo PDF
                </button>
              </div>
              <div style={{ marginBottom:'10px'}}>
                <label>
                  Päivä
                  <input
                    type="date"
                    value={selectedDay}
                    onChange={(e)=> setSelectedDay(e.target.value)}
                    style={{ marginLeft:'5px', marginRight:'10px'}}
                  />
                </label>
                <button onClick={handleFetchHourly}>Hae tunnit</button>
              </div>

              {hourlyData.length>0 && (
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={hourlyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#666"/>
                      <XAxis dataKey="hourNum" stroke="#eee"/>
                      <YAxis stroke="#eee"/>
                      <Tooltip
                        contentStyle={{ backgroundColor:'#222', border:'none'}}
                        labelStyle={{ color:'#fff'}}
                        itemStyle={{ color:'#fff'}}
                      />
                      <Bar dataKey="eventCount" fill="#ff3636" name="Kävijät">
                        <LabelList
                          dataKey="eventCount"
                          position="insideBottom"
                          offset={6}
                          fill="#fff"
                          fontSize={12}
                          formatter={(v) => (v > 0 ? v : '')}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Asetukset */}
      {showSettingsModal && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <h2>Asetukset</h2>
            <p>Voit muokata aukioloaikoja + käyttöönottopäivää (tallentuu localStorageen).</p>
            <div className="openhours-grid-improved">
              {[
                { dayKey:'monday', label:'Maanantai'},
                { dayKey:'tuesday', label:'Tiistai'},
                { dayKey:'wednesday', label:'Keskiviikko'},
                { dayKey:'thursday', label:'Torstai'},
                { dayKey:'friday', label:'Perjantai'},
                { dayKey:'saturday', label:'Lauantai'},
                { dayKey:'sunday', label:'Sunnuntai'}
              ].map(d=>(
                <div key={d.dayKey} className="openhours-row-improved">
                  <label>{d.label}</label>
                  <div className="time-inputs">
                    <input
                      type="number"
                      name={`${d.dayKey}Open`}
                      min={0}
                      max={23}
                      value={openHours[`${d.dayKey}Open`]||''}
                      onChange={handleOpenHoursChange}
                    />
                    <span>-</span>
                    <input
                      type="number"
                      name={`${d.dayKey}Close`}
                      min={0}
                      max={24}
                      value={openHours[`${d.dayKey}Close`]||''}
                      onChange={handleOpenHoursChange}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop:'15px'}}>
              <label>Käyttöönotto-päivä:</label><br/>
              <input
                type="date"
                value={usageDate}
                onChange={handleUsageDateChange}
                style={{ marginTop:'5px'}}
              />
              <p style={{ fontSize:'0.9em', color:'#ccc'}}>
                Data ennen tätä päivää ei näy
              </p>
            </div>
            <div className="modal-buttons improved-buttons" style={{ marginTop:'15px'}}>
              <button onClick={handleSaveSettings}>Tallenna</button>
              <button onClick={handleCloseModal}>Sulje</button>
            </div>
          </div>
        </div>
      )}

      {/* KONVERSIOLASKURI */}
      {showConversion && (
        <div className="modal-backdrop">
          <div className="modal-content conversion-modal" id="conversion-calculator">
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center'}}>
              <h2>Konversiolaskuri</h2>
              <div style={{ display:'flex', gap:'10px'}}>
                <button onClick={()=> handleExportPdf('conversion-calculator','Konversiolaskuri.pdf')}>
                  Luo PDF
                </button>
                <button onClick={closeConversionCalculator}>Sulje</button>
              </div>
            </div>

            <div className="conversion-body">
              {/* vasen: lomakkeet */}
              <div className="conversion-panel">
                <p style={{ marginBottom:'10px'}}>
                  <strong>Päiväkohtainen konversio:</strong> Valitse päivä → syötä myyntitapahtumat → Laske
                </p>
                <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'15px'}}>
                  <label style={{ minWidth:'80px'}}>Päivä</label>
                  <input
                    type="date"
                    value={convDate}
                    onChange={(e)=> setConvDate(e.target.value)}
                    style={{ flex:'1'}}
                  />
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'15px'}}>
                  <label style={{ minWidth:'80px'}}>Myyntitapahtumat</label>
                  <input
                    type="number"
                    value={convSales}
                    onChange={(e)=> setConvSales(e.target.value)}
                    style={{ width:'100px'}}
                  />
                </div>
                <button onClick={handleCalculateDayConversion}>Laske</button>

                <hr style={{ margin:'20px 0'}}/>

                <p style={{ marginBottom:'10px'}}>
                  <strong>Konversio ajanjaksolta:</strong> Valitse alku & loppu → Syötä myynti → Laske
                </p>
                <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'15px'}}>
                  <label style={{ minWidth:'60px'}}>Alku</label>
                  <input
                    type="date"
                    value={convStart}
                    onChange={(e)=> setConvStart(e.target.value)}
                    style={{ flex:'1'}}
                  />
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'15px'}}>
                  <label style={{ minWidth:'60px'}}>Loppu</label>
                  <input
                    type="date"
                    value={convEnd}
                    onChange={(e)=> setConvEnd(e.target.value)}
                    style={{ flex:'1'}}
                  />
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'15px'}}>
                  <label style={{ minWidth:'80px'}}>Myyntitapahtumat</label>
                  <input
                    type="number"
                    value={convRangeSales}
                    onChange={(e)=> setConvRangeSales(e.target.value)}
                    style={{ width:'100px'}}
                  />
                </div>
                <button onClick={handleCalculateRangeConversion}>Laske</button>
              </div>

              {/* oikea: taulukko */}
              <div className="conversion-panel">
                <h3>Konversiotulokset</h3>
                <div style={{ overflowX:'auto', marginTop:'10px'}}>
                  <table style={{ width:'100%', borderCollapse:'collapse', tableLayout:'fixed' }}>
                    <colgroup>
                      <col style={{ width:'80px'}}/>
                      <col style={{ width:'120px'}}/>
                      <col style={{ width:'180px'}}/>
                      <col style={{ width:'80px'}}/>
                      <col style={{ width:'80px'}}/>
                      <col style={{ width:'120px'}}/>
                      <col style={{ width:'80px'}}/>
                    </colgroup>
                    <thead>
                      <tr style={{ borderBottom:'1px solid #777'}}>
                        <th style={{ padding:'8px'}}>Tyyppi</th>
                        <th style={{ padding:'8px'}}>Sijainti</th>
                        <th style={{ padding:'8px'}}>Päivä/Ajanjakso</th>
                        <th style={{ padding:'8px'}}>Kävijät</th>
                        <th style={{ padding:'8px'}}>Myynti</th>
                        <th style={{ padding:'8px'}}>Konversio %</th>
                        <th style={{ padding:'8px'}}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {conversionHistory.map((c,i)=> {
                        const niceDate= formatDateOrRange(c.dateOrRange, c.mode);
                        return (
                          <tr key={i} style={{ borderBottom:'1px solid #444'}}>
                            <td style={{ padding:'6px'}}>{c.mode==='day' ? 'Päivä':'Ajanjakso'}</td>
                            <td style={{ padding:'6px'}}>{c.storeName}</td>
                            <td style={{ padding:'6px'}}>{niceDate}</td>
                            <td style={{ padding:'6px'}}>{c.visitors}</td>
                            <td style={{ padding:'6px'}}>{c.sales}</td>
                            <td style={{ padding:'6px'}}>{c.conversion}%</td>
                            <td style={{ padding:'6px', textAlign:'center'}}>
                              <button className="delete-button" onClick={()=> handleRemoveConversion(i)}>
                                Poista
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
